import { Injectable } from '@angular/core';
import { ConfigService } from 'src/app/modules/core/services/config.service';

@Injectable({
	providedIn: 'root'
})
export class StorageService {
	readonly _storage: Storage;
	private _keyPrefix: string = this.configService.config.storagePrefix;
	// can be set to avoid reset/rewrite keys in LocalStorage
	private _excludeKeys: string[] = [];

	constructor(private configService: ConfigService) {
		this._storage = localStorage;
	}

	/**
	 * Clear the whole localstorage
	 */
	clear(): void {
		this._storage.clear();
	}

	/**
	 * remove single item from storage
	 *
	 * @param key
	 */
	removeItem(key: string): void {
		key = this.addPrefix(key);
		return this._storage.removeItem(key);
	}

	/**
	 * Set Key in storage and check if is in storage and override if override: true
	 * Be careful: If a key has been excluded it won't be renewed
	 * See 'excludeKeys'
	 *
	 * @param key
	 * @param item
	 * @param override
	 */
	setItem(key: string, item: any, override: boolean = true) {
		key = this.addPrefix(key);

		if (!this._storage.getItem(key)) {
			const data = typeof item !== 'string' ? JSON.stringify(item) : item;
			this._storage.setItem(key, data);
		} else if (this._storage.getItem(key) && override && !(this.excludeKeys.find((x) => this.addPrefix(x) === key) != null)) {
			const data = typeof item !== 'string' ? JSON.stringify(item) : item;
			this._storage.setItem(key, data);
		}
	}

	/**
	 * Get item from localstorage
	 *
	 * @param key
	 * @param convert
	 * @returns
	 */
	getItem(key: string, convert?: (item: string) => any): any {
		key = this.addPrefix(key);
		const data = this._storage.getItem(key);
		if (convert) {
			return convert(data);
		} else if (data && (data.startsWith('{') || data.startsWith('['))) {
			return JSON.parse(data);
		}

		return data;
	}

	/**
	 * get all keys stored in localstorage
	 *
	 * @returns
	 */
	keys(): string[] {
		const keys: string[] = [];

		if (this._storage) {
			for (let index = 0; index < this._storage.length; index++) {
				const key = this.removePrefix(this._storage.key(index));
				keys.push(key);
			}
		}

		return keys;
	}

	/**
	 * Get key by index
	 *
	 * @param index
	 * @returns
	 */
	key(index: number): string {
		return this._storage ? this.removePrefix(this._storage.key(index)) : null;
	}

	/**
	 * Get value by index
	 *
	 * @param index
	 * @param convert
	 * @returns
	 */
	value(index: number, convert?: (item: string) => any): any {
		const key = this._storage ? this.removePrefix(this._storage.key(index)) : null;

		if (key) {
			return this.getItem(key, convert);
		}

		return null;
	}

	/**
	 * getter for length property
	 *
	 * @returns
	 */
	get length(): number {
		return this._storage ? this._storage.length : 0;
	}

	/**
	 * Adds the prefix to the given key
	 *
	 * @param key Key
	 */
	private addPrefix(key: string) {
		if (!key || key.length === 0 || !this._keyPrefix || this._keyPrefix.length === 0) {
			return key;
		}

		key = `${this._keyPrefix}${key}`;

		return key;
	}

	/**
	 * Removes the prefix from the given key
	 *
	 * @param key Key
	 */
	private removePrefix(key: string) {
		if (!key || key.length === 0 || !this._keyPrefix || this._keyPrefix.length === 0) {
			return key;
		}

		if (key.startsWith(this._keyPrefix)) {
			key = key.substr(this._keyPrefix.length);
		}

		return key;
	}

	/**
	 * Exclude some Keys to not override them
	 * use this setter Function to avoid set Keys in LocalStorage
	 * excludeKeys will be used in setItem Function
	 * Exclude some Keys to not override them
	 *
	 * @param value
	 */
	set excludeKeys(value: string[]) {
		this._excludeKeys = value;
	}

	/**
	 * Get Current List of excludedKeys
	 */
	get excludeKeys(): string[] {
		return this._excludeKeys;
	}
}
